import { useCallback, useEffect, useRef, useState } from "react";
import { Popover, Stack, Tooltip, Typography } from "@mui/material";

interface IDescriptionPopoverProps {
	descriptions: string[];
	itemsCounter: string;
}

const FONT_SIZE = "12px";
const FONT_FAMILY = "sans-serif";
const MIN_TEXT_WIDTH = 30;
const BASIC_PADDING = 20;
const COUNTER_SPACING = 15;
const MIN_RESERVED_SPACE = 35;
const FALLBACK_CHAR_WIDTH = 7;

const DescriptionPopover = (props: IDescriptionPopoverProps) => {
	const { descriptions, itemsCounter } = props;
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const descriptionRef = useRef<HTMLSpanElement>(null);
	const [displayText, setDisplayText] = useState("");
	const [isTruncated, setIsTruncated] = useState(false);

	// Utility function to create and measure text width
	const measureTextWidth = useCallback((text: string): number => {
		const tempElement = document.createElement("span");
		Object.assign(tempElement.style, {
			visibility: "hidden",
			position: "absolute",
			fontSize: FONT_SIZE,
			fontFamily: FONT_FAMILY,
			whiteSpace: "nowrap",
		});
		tempElement.textContent = text;
		document.body.appendChild(tempElement);
		const width = tempElement.offsetWidth;
		document.body.removeChild(tempElement);
		return width;
	}, []);

	// Binary search for optimal truncation
	const findOptimalTruncation = useCallback(
		(text: string, maxWidth: number): string => {
			let left = 0;
			let right = text.length;
			let bestFit = "";

			while (left <= right) {
				const mid = Math.floor((left + right) / 2);
				const testText = text.substring(0, mid) + "...";
				const width = measureTextWidth(testText);

				if (width <= maxWidth) {
					bestFit = testText;
					left = mid + 1;
				} else {
					right = mid - 1;
				}
			}

			return bestFit || "...";
		},
		[measureTextWidth]
	);

	// Main calculation function
	const calculateDisplayText = useCallback(() => {
		if (!descriptions[0] || !descriptionRef.current) {
			return;
		}

		const containerElement = descriptionRef.current.parentElement;
		if (!containerElement) return;

		const containerWidth = containerElement.getBoundingClientRect().width;
		const originalText = descriptions[0].trim();

		// Single item or no counter - use full width
		if (descriptions.length <= 1 || !itemsCounter) {
			const availableWidth = Math.max(containerWidth - BASIC_PADDING, MIN_TEXT_WIDTH * 2);
			const textWidth = measureTextWidth(originalText);

			if (textWidth <= availableWidth) {
				setDisplayText(originalText);
				setIsTruncated(false);
			} else {
				setDisplayText(findOptimalTruncation(originalText, availableWidth));
				setIsTruncated(true);
			}
			return;
		}

		// Multiple items - reserve space for counter
		const counterWidth = measureTextWidth(itemsCounter) || itemsCounter.length * FALLBACK_CHAR_WIDTH;
		const reservedSpace = Math.max(counterWidth + COUNTER_SPACING, MIN_RESERVED_SPACE);
		const availableWidth = Math.max(containerWidth - reservedSpace, MIN_TEXT_WIDTH);

		const textWidth = measureTextWidth(originalText);

		if (textWidth <= availableWidth) {
			setDisplayText(originalText);
			setIsTruncated(false);
		} else {
			setDisplayText(findOptimalTruncation(originalText, availableWidth));
			setIsTruncated(true);
		}
	}, [descriptions, itemsCounter, measureTextWidth, findOptimalTruncation]);

	useEffect(() => {
		// Initial calculation with delay to ensure DOM is ready
		const initialTimeout = setTimeout(calculateDisplayText, 100);

		// Window resize listener with debouncing
		let resizeTimeout: NodeJS.Timeout;
		const handleResize = () => {
			clearTimeout(resizeTimeout);
			resizeTimeout = setTimeout(calculateDisplayText, 50);
		};
		window.addEventListener("resize", handleResize);

		// Container resize observer with debouncing
		let resizeObserver: ResizeObserver | null = null;
		let observerTimeout: NodeJS.Timeout;
		const parentElement = descriptionRef.current?.parentElement;

		if (parentElement && ResizeObserver) {
			const handleObserverResize = () => {
				clearTimeout(observerTimeout);
				observerTimeout = setTimeout(calculateDisplayText, 50);
			};

			resizeObserver = new ResizeObserver(handleObserverResize);
			resizeObserver.observe(parentElement);
		}

		return () => {
			clearTimeout(initialTimeout);
			clearTimeout(resizeTimeout);
			clearTimeout(observerTimeout);
			window.removeEventListener("resize", handleResize);
			resizeObserver?.disconnect();
		};
	}, [calculateDisplayText]);

	const handleClick = useCallback(() => {
		setAnchorEl(descriptionRef.current);
	}, []);

	const handleClose = useCallback(() => {
		setAnchorEl(null);
	}, []);

	const open = Boolean(anchorEl);
	const id = open ? "simple-popover" : undefined;
	const showTooltip = isTruncated && descriptions[0];
	const showCounter = displayText && itemsCounter;

	return (
		<>
			<Tooltip arrow title={showTooltip ? descriptions[0] : ""} disableHoverListener={!showTooltip}>
				<Typography
					aria-describedby={id}
					className="!text-xs !text-default !font-sans !inline-flex !items-center !overflow-hidden !whitespace-nowrap"
					component="span"
					ref={descriptionRef}
				>
					{displayText}
				</Typography>
			</Tooltip>

			{showCounter && (
				<>
					<Typography className="!text-xs !text-default !font-sans" component="span">
						,
					</Typography>
					<Typography onClick={handleClick} className="!text-xs !font-sans link-look !ml-[3px] cursor-pointer" component="span">
						{itemsCounter}
					</Typography>
				</>
			)}

			<Popover
				id={id}
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
			>
				<Stack className="!p-2" spacing={0.25}>
					{descriptions.map((description, index) => (
						<Typography key={`${description}-${index}`} className="!text-sm !font-sans !text-default" component="span">
							{description}
						</Typography>
					))}
				</Stack>
			</Popover>
		</>
	);
};

export default DescriptionPopover;
